// components/Header.tsx
import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { signInWithGoogle, signOut } from '../services/auth';
import { Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

interface HeaderProps {
  user: User | null;
  onError?: (error: string) => void;
  isFixed?: boolean;
}

// Using React.memo with a custom comparison function to prevent unnecessary re-renders
export const Header = memo(function Header({ 
  user, 
  onError, 
  isFixed = false 
}: HeaderProps) {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      onError?.('Failed to sign in with Google. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      onError?.('Failed to sign out. Please try again.');
    }
  };

  return (
    <div className={`h-16 md:h-24 flex justify-between items-center mb-4 md:mb-8 max-w-6xl mx-auto w-full px-4 py-4 md:py-6 ${isFixed ? 'fixed top-0 left-0 right-0 bg-background z-50' : ''}`}>
      <Link to="/" className="h-full flex items-center py-2"> 
        <img 
          src="/wsu-logo.png" 
          alt="WhatSayUs Logo" 
          className="h-8 md:h-10 w-auto object-contain"
        />
      </Link>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-4">
        {user ? (
          <div className="flex items-center gap-4">
            <span className="text-sm">{user.displayName}</span>
            <Button variant="outline" onClick={handleSignOut}>Sign Out</Button>
          </div>
        ) : (
          <Button onClick={handleSignIn}>Sign in with Google</Button>
        )}
      </div>

      {/* Mobile Menu */}
      <div className="md:hidden h-full flex items-center"> 
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-12 w-12">
              <Menu className="h-7 w-7" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-72 p-3 border rounded-md shadow-md bg-white mr-2">
            {user ? (
              <>
                <DropdownMenuItem className="px-3 py-2.5 text-base text-muted-foreground">
                  Signed in as {user.displayName}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="px-3 py-2.5 text-base cursor-pointer hover:bg-accent hover:text-accent-foreground mt-1"
                  onClick={handleSignOut}
                >
                  Sign Out
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem 
                className="px-3 py-2.5 text-base cursor-pointer hover:bg-accent hover:text-accent-foreground"
                onClick={handleSignIn}
              >
                Sign in with Google
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render when relevant props change
  return (
    prevProps.isFixed === nextProps.isFixed && 
    prevProps.user?.uid === nextProps.user?.uid
  );
});

export default Header;